import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input, type InputProps } from './Input';

export type PasswordInputProps = Omit<InputProps, 'type' | 'trailingAddon'>;

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden className="size-5">
      <path
        d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="1.6" />
      {off && <path d="m4 4 16 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />}
    </svg>
  );
}

/**
 * Password field with a reveal toggle.
 *
 * The toggle is a real `<button>` with an `aria-label` and `aria-pressed`, not a
 * clickable `<span>` — screen-reader and keyboard users get the same affordance.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(props, ref) {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);

    return (
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        autoComplete="current-password"
        trailingAddon={
          <button
            type="button"
            onClick={() => {
              setVisible((v) => !v);
            }}
            aria-pressed={visible}
            aria-label={visible ? t('auth.hidePassword') : t('auth.showPassword')}
            className="grid size-8 place-items-center rounded-xs text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
          >
            <EyeIcon off={visible} />
          </button>
        }
        {...props}
      />
    );
  }
);
